import React, { useState, useEffect, useRef } from 'react';
import './DatePicker.css';

interface DatePickerProps {
  value?: string; // Expects ISO string 'YYYY-MM-DD'
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

const WEEKDAY_NAMES = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = 'TT.MM.JJJJ',
  className = 'wizard-input',
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Parse YYYY-MM-DD to Date object or fallback to today
  const parseIsoDate = (isoStr?: string): Date | null => {
    if (!isoStr) return null;
    const parts = isoStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month, day);
      }
    }
    return null;
  };

  const initialDate = parseIsoDate(value) || new Date();
  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  // Format YYYY-MM-DD to DD.MM.YYYY
  const isoToDisplay = (isoStr?: string): string => {
    if (!isoStr) return '';
    const parts = isoStr.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    }
    return isoStr;
  };

  // Format DD.MM.YYYY or typed input to YYYY-MM-DD
  const displayToIso = (text: string): string | undefined => {
    const trimmed = text.trim();
    if (!trimmed) return undefined;
    const parts = trimmed.split('.');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (
        !isNaN(day) &&
        !isNaN(month) &&
        !isNaN(year) &&
        day >= 1 &&
        day <= 31 &&
        month >= 1 &&
        month <= 12 &&
        year >= 1900 &&
        year <= 2100
      ) {
        const mm = String(month).padStart(2, '0');
        const dd = String(day).padStart(2, '0');
        return `${year}-${mm}-${dd}`;
      }
    }
    return undefined;
  };

  useEffect(() => {
    setDisplayText(isoToDisplay(value));
    const parsed = parseIsoDate(value);
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setDisplayText(val);
    const iso = displayToIso(val);
    if (iso !== undefined || val === '') {
      onChange(iso);
    }
  };

  const handleInputBlur = () => {
    if (!displayText) {
      onChange(undefined);
    } else {
      const iso = displayToIso(displayText);
      if (iso) {
        onChange(iso);
        setDisplayText(isoToDisplay(iso));
      } else {
        // Revert to valid value or clear if invalid
        setDisplayText(isoToDisplay(value));
      }
    }
  };

  const handlePrevMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDay = (year: number, month: number, day: number) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    const iso = `${year}-${mm}-${dd}`;
    onChange(iso);
    setDisplayText(isoToDisplay(iso));
    setIsOpen(false);
  };

  const handleSelectToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    handleSelectDay(year, month, day);
  };

  const handleClear = () => {
    onChange(undefined);
    setDisplayText('');
    setIsOpen(false);
  };

  // Build days grid for calendar view
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // 0 = Mo, 6 = So
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

  const selectedDate = parseIsoDate(value);
  const today = new Date();

  // Generating years list for quick select (1960 - currentYear + 10)
  const currentYear = new Date().getFullYear();
  const yearOptions: number[] = [];
  for (let y = currentYear + 10; y >= 1960; y--) {
    yearOptions.push(y);
  }

  return (
    <div className="valuera-datepicker-wrapper" ref={wrapperRef}>
      <div className="wizard-field-input-box">
        <input
          type="text"
          className={className}
          style={{ paddingRight: '48px', ...style }}
          placeholder={placeholder}
          value={displayText}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
        />
        <span
          className="wizard-input-icon"
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="valuera-datepicker-popover">
          <div className="valuera-datepicker-header">
            <button
              type="button"
              className="valuera-datepicker-nav-btn"
              onClick={handlePrevMonth}
            >
              &#8249;
            </button>

            <div className="valuera-datepicker-title">
              <select
                className="valuera-datepicker-select"
                value={viewMonth}
                onChange={(e) => setViewMonth(parseInt(e.target.value, 10))}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={idx} value={idx}>
                    {name}
                  </option>
                ))}
              </select>

              <select
                className="valuera-datepicker-select"
                value={viewYear}
                onChange={(e) => setViewYear(parseInt(e.target.value, 10))}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="valuera-datepicker-nav-btn"
              onClick={handleNextMonth}
            >
              &#8250;
            </button>
          </div>

          <div className="valuera-datepicker-grid">
            {WEEKDAY_NAMES.map((dayName) => (
              <div key={dayName} className="valuera-datepicker-weekday">
                {dayName}
              </div>
            ))}

            {/* Days from previous month */}
            {Array.from({ length: firstDayOfWeek }).map((_, idx) => {
              const dayNum = prevMonthDays - firstDayOfWeek + idx + 1;
              return (
                <div
                  key={`prev-${idx}`}
                  className="valuera-datepicker-day other-month"
                >
                  {dayNum}
                </div>
              );
            })}

            {/* Days of current month */}
            {Array.from({ length: daysInMonth }).map((_, idx) => {
              const dayNum = idx + 1;
              const isSelected =
                selectedDate &&
                selectedDate.getFullYear() === viewYear &&
                selectedDate.getMonth() === viewMonth &&
                selectedDate.getDate() === dayNum;

              const isToday =
                today.getFullYear() === viewYear &&
                today.getMonth() === viewMonth &&
                today.getDate() === dayNum;

              return (
                <div
                  key={`day-${dayNum}`}
                  className={`valuera-datepicker-day ${
                    isSelected ? 'selected' : ''
                  } ${isToday ? 'today' : ''}`}
                  onClick={() => handleSelectDay(viewYear, viewMonth, dayNum)}
                >
                  {dayNum}
                </div>
              );
            })}
          </div>

          <div className="valuera-datepicker-footer">
            <button
              type="button"
              className="valuera-datepicker-btn-flat"
              onClick={handleClear}
            >
              Löschen
            </button>
            <button
              type="button"
              className="valuera-datepicker-btn-flat"
              onClick={handleSelectToday}
            >
              Heute
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
